import { DEFAULT_LOCALE } from "@/i18n/routing";
import { IMAGE_FIELDS } from "../common";
import { CONTENT_STATUS } from "@/constants";

/**
 * Minimal query config for counting hotels via the /japan-areas REST endpoint.
 * Only fetches documentId for areas and their hotels — no images, SEO, or properties.
 * Used in useHotelCount to get a fast count matching japanAreas.flatMap(a => a.hotels).length.
 */
export const getJapanAreasCountQueryConfig = (status?: string) => ({
  fields: ["documentId"],
  locale: DEFAULT_LOCALE,
  populate: {
    hotels: {
      fields: ["documentId"],
    },
  },
  pagination: { limit: 1000 },
  status: (status ?? CONTENT_STATUS.PUBLISHED).toLowerCase(),
});

/**
 * Returns the configuration object for fetching Japan Areas data.
 * Supports hotels, attractions, SEO images, datasources, and key stations.
 * Conditionally applies `targetLanguage` for image fields if different from default.
 */
export const getJapanAreasQueryConfig = (
  targetLanguage: string,
  status?: string,
) => ({
  // case Japan Areas is always need locale
  locale: targetLanguage,
  fields: [
    "id",
    "documentId",
    "item_name",
    "area_name",
    "longitude",
    "latitude",
    "title",
    "map_zoom_ratio",
    "state_code",
    "slug_hotel",
    "slug_attraction",
    "selected_hotels",
  ],
  populate: {
    cover_image: {
      fields: IMAGE_FIELDS,
      ...(targetLanguage == DEFAULT_LOCALE ? {} : { targetLanguage }),
    },
    attractions: {
      fields: ["id", "documentId", "item_name", "slug"],
      populate: {
        properties: {
          fields: ["name", "latitude", "longitude"],
          populate: {
            overview_image: {
              fields: IMAGE_FIELDS,
              ...(targetLanguage == DEFAULT_LOCALE ? {} : { targetLanguage }),
            },
          },
        },
      },
    },
    hotels: {
      populate: {
        seo: true,
        amenities_and_services: true,
        external_ids: true,
        hotel_images: { fields: ["documentId"] },
        datasources: {
          populate: {
            slider_datasources: {
              populate: {
                information: {
                  fields: ["picked_images_desktop", "picked_images_mobile"],
                },
              },
            },
          },
        },
        redirect: true,
        properties: true,
        weather: true,
        force_default_no_of_guest: true,
      },
    },
    key_stations_marked_cities: {
      fields: ["item_name"],
      populate: {
        icon: {
          fields: ["cloudflare_url"],
        },
        preview_image: {
          fields: ["cloudflare_url"],
        },
      },
    },
  },
  filters: {
    area_name: {
      $ne: "$name",
      $notNull: true,
    },
    hotels: {
      properties: {
        hotel_name: {
          $notNull: true,
          $ne: "$name",
        },
      },
    },
  },
  pagination: { limit: 100 },
  status: (status || CONTENT_STATUS.DRAFT).toLowerCase(),
});
